import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatNumber } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { useFeature } from '../app/entitlements';
import { listPages, type PageView } from './api';
import { createPage } from './pages-api';

/** Landing pages and funnels (context.md sections 72-77). */

const TONE: Record<string, BadgeTone> = {
  PUBLISHED: 'positive',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

export function PagesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const pages = useQuery({ queryKey: ['pages'], queryFn: () => listPages() });
  const { allowed, planName } = useFeature('LANDING_PAGES');

  const columns: Column<PageView>[] = [
    {
      key: 'name',
      header: t('pages.title'),
      render: (page) => (
        <span className="cell-stack">
          <span className="strong">{page.name}</span>
          <span className="muted small">
            <code>/{page.slug}</code>
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (page) => t(`pages.state.${page.status}`),
      render: (page) => (
        <Badge tone={TONE[page.status] ?? 'neutral'}>{t(`pages.state.${page.status}`)}</Badge>
      ),
    },
    {
      key: 'visits',
      header: t('pages.visits'),
      align: 'end',
      text: (page) => String(page.visitCount),
      render: (page) => formatNumber(page.visitCount),
    },
    {
      /*
       * Visits and conversions side by side, with the rate under them.
       *
       * A landing page has exactly one job and this is the column that says whether it is doing
       * it. A count of visits alone flatters a page nobody acts on.
       */
      key: 'conversions',
      header: t('pages.conversions'),
      align: 'end',
      text: (page) => String(page.conversionCount),
      render: (page) => (
        <span className="cell-stack cell-stack--end">
          <span>{formatNumber(page.conversionCount)}</span>
          <span className="muted small">{rate(page)}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      text: () => '',
      render: (page) => (
        <span className="cell-inline">
          {page.publicUrl && (
            <a
              className="button button--quiet button--compact"
              href={page.publicUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={(event) => event.stopPropagation()}
            >
              {t('pages.open')}
            </a>
          )}
          <Link
            className="button button--quiet button--compact"
            to={`/marketing/paginas/${page.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {t('pages.edit')}
          </Link>
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('pages.kind'),
      hidden: true,
      text: (page) => t(`pages.kind.${page.kind}`),
      render: (page) => t(`pages.kind.${page.kind}`),
    },
    {
      key: 'slug',
      header: t('pages.slug'),
      hidden: true,
      render: (page) => <code>{page.slug}</code>,
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.pages')}
        description={t('pages.description')}
        actions={
          allowed === false ? (
            <Badge tone="warning">{t('pages.notInPlan').replace('{plan}', planName ?? '')}</Badge>
          ) : (
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={allowed === undefined}
              onClick={() => setCreating(true)}
            >
              {t('pages.new')}
            </button>
          )
        }
      />

      {creating && (
        <NewPage
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/marketing/paginas/${id}`)}
        />
      )}

      <Panel>
        <DataTable
          tableKey="pages"
          rows={pages.data?.pages}
          columns={columns}
          rowKey={(page) => page.id}
          isLoading={pages.isLoading}
          error={pages.error}
          onRetry={() => void pages.refetch()}
          onRowClick={(page) => navigate(`/marketing/paginas/${page.id}`)}
          empty={{ title: t('pages.empty'), description: t('pages.emptyBody') }}
        />
      </Panel>
    </div>
  );
}

function rate(page: PageView): string {
  if (page.visitCount === 0) return '—';

  return `${((page.conversionCount / page.visitCount) * 100).toFixed(1)}%`;
}

/**
 * Starting a page: a name and the address it lives at.
 *
 * The slug is derived from the name, because it is the part people get wrong — a space or an
 * accent in a URL is a URL that breaks somewhere — and stays editable for whoever cares.
 */
function NewPage({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createPage({ name: name.trim(), slug: slug.trim() }),
    onSuccess: (page) => {
      void client.invalidateQueries({ queryKey: ['pages'] });
      onClose();
      onCreated(page.id);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Panel title={t('pages.new')}>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() && slug.trim()) create.mutate();
        }}
      >
        <label className="field-search">
          <input
            value={name}
            autoFocus
            maxLength={200}
            placeholder={t('pages.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              if (!touched) setSlug(slugify(event.target.value));
            }}
          />
        </label>

        <label className="cell-inline small">
          <span className="muted">/p/…/</span>
          <input
            value={slug}
            maxLength={63}
            aria-label={t('pages.slug')}
            onChange={(event) => {
              setTouched(true);
              setSlug(slugify(event.target.value));
            }}
          />
        </label>

        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={name.trim() === '' || slug.length < 3 || create.isPending}
        >
          {create.isPending ? t('work.creating') : t('pages.create')}
        </button>

        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('confirm.cancel')}
        </button>
      </form>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </Panel>
  );
}

/** The server's slug rule, applied here so the field cannot hold something it will refuse. */
export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}
